import { Injectable } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Cookie } from 'ng2-cookies';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/filter';
import { EesDesService } from './ees-des.service';
import { MultiCompanyViewTokenService } from './multi-company-view-token.service';
import { WebServiceService } from './web-service.service';
import { LocalStorageService } from './storage/local-storage.service';

export const GROUP_MAILED_INVOICE_HOME_CONTEXT_KEY = 'group_mailed_invoice_home_company_context';
export const GROUP_MAILED_INVOICE_SKIP_ROLE_CHECK_KEY = 'group_mailed_invoice_skip_role_check';
export const TYA_COMPANY_HEADER_REFRESH_EVENT = 'tyaCompanyHeaderRefresh';

export interface GroupMailedInvoiceCompanyRecord {
  company_id: string | number;
  company_title?: string;
  company_name?: string;
  company_code?: string;
  company_logo?: string;
  company_theme?: string;
  company_theme_text?: string;
}

interface HomeCompanyContext {
  company_id: string;
  company_title: string;
  company_code: string;
  company_logo: string;
  company_theme: string;
  company_theme_text: string;
  emp_role: string;
  employee_code: string;
  access_token: string;
  user_access_token: string;
  session_company_id: string;
}

@Injectable()
export class GroupMailedInvoiceCompanyContextService {
  private isRestoringHome = false;
  private restoreCompleteCallbacks: Array<() => void> = [];

  constructor(
    private eesdesService: EesDesService,
    private webService: WebServiceService,
    private multiCompanyViewTokenService: MultiCompanyViewTokenService,
    private localStorageService: LocalStorageService,
    private router: Router
  ) {
    this.router.events
      .filter(event => event instanceof NavigationStart)
      .subscribe((event: NavigationStart) => {
        const url = event.url || '';
        // Restore login company when returning to the group mailed invoice list.
        // Invoice pages call restoreBeforeLeavingInvoicePage explicitly on Back.
        if (url.indexOf('group-mailed-invoice') !== -1) {
          this.restoreHomeCompanyIfPresent();
        }
      });
  }

  /** Call before leaving an invoice page when returning to company invoices. */
  restoreBeforeLeavingInvoicePage(onComplete?: () => void): void {
    this.restoreHomeCompanyIfPresent(onComplete);
  }

  static shouldSkipRoleCheck(): boolean {
    const raw = sessionStorage.getItem(GROUP_MAILED_INVOICE_SKIP_ROLE_CHECK_KEY);
    if (!raw) {
      return false;
    }
    const until = parseInt(raw, 10);
    if (isNaN(until) || Date.now() >= until) {
      sessionStorage.removeItem(GROUP_MAILED_INVOICE_SKIP_ROLE_CHECK_KEY);
      return false;
    }
    return true;
  }

  getCurrentCompanyId(): string {
    const id = this.eesdesService.get('company_id', localStorage.getItem('company_id'));
    return id != undefined && id != null && id !== '' ? id.toString() : '';
  }

  hasPendingHomeRestore(): boolean {
    return !!localStorage.getItem(GROUP_MAILED_INVOICE_HOME_CONTEXT_KEY);
  }

  restoreHomeCompanyIfPresent(onComplete?: () => void): boolean {
    if (onComplete) {
      this.restoreCompleteCallbacks.push(onComplete);
    }

    const flushCallbacks = () => {
      const callbacks = this.restoreCompleteCallbacks.slice();
      this.restoreCompleteCallbacks = [];
      callbacks.forEach(cb => {
        try {
          cb();
        } catch (e) {}
      });
    };

    if (this.isRestoringHome) {
      return true;
    }

    const raw = localStorage.getItem(GROUP_MAILED_INVOICE_HOME_CONTEXT_KEY);
    if (!raw) {
      flushCallbacks();
      return false;
    }
    try {
      const ctx: HomeCompanyContext = JSON.parse(raw);
      const homeCompanyId = ctx.company_id != null ? ctx.company_id.toString() : '';
      if (!homeCompanyId) {
        localStorage.removeItem(GROUP_MAILED_INVOICE_HOME_CONTEXT_KEY);
        flushCallbacks();
        return false;
      }

      this.isRestoringHome = true;

      const companyRecord: GroupMailedInvoiceCompanyRecord = {
        company_id: homeCompanyId,
        company_title: ctx.company_title,
        company_code: ctx.company_code,
        company_logo: ctx.company_logo,
        company_theme: ctx.company_theme,
        company_theme_text: ctx.company_theme_text,
      };

      const finishRestore = (
        accessToken: string,
        userAccessToken: string,
        empRole: string,
        employeeCode: string
      ) => {
        this.applyCompanyStorage(
          companyRecord,
          accessToken,
          userAccessToken,
          empRole,
          employeeCode,
          true
        );
        sessionStorage.setItem('company_id', ctx.session_company_id || homeCompanyId);
        this.markRoleCheckSkipped();
        localStorage.removeItem(GROUP_MAILED_INVOICE_HOME_CONTEXT_KEY);
        this.multiCompanyViewTokenService.clearCache();
        this.webService.clearApiCache();
        this.notifyHeaderRefresh();
        this.isRestoringHome = false;
        flushCallbacks();
      };

      const runRestore = (empRole: string, employeeCode: string) => {
        this.markRoleCheckSkipped();
        // Fetch fresh home-company tokens WHILE invoice-company auth is still valid.
        // Applying saved tokens first can break the session if generate_token invalidated them.
        this.multiCompanyViewTokenService.clearCache();
        this.multiCompanyViewTokenService.getOrFetchTokens(homeCompanyId).subscribe(
          tokens => {
            finishRestore(
              tokens.access_token || ctx.access_token,
              tokens.user_access_token || ctx.user_access_token,
              empRole,
              employeeCode
            );
          },
          () => {
            finishRestore(ctx.access_token, ctx.user_access_token, empRole, employeeCode);
          }
        );
      };

      if (ctx.emp_role) {
        runRestore(ctx.emp_role, ctx.employee_code);
      } else {
        this.fetchEmployeeCompanyDetails(homeCompanyId).subscribe(
          details => runRestore(details.employee_role, details.employee_code),
          () => runRestore(ctx.emp_role, ctx.employee_code)
        );
      }
      return true;
    } catch (e) {
      localStorage.removeItem(GROUP_MAILED_INVOICE_HOME_CONTEXT_KEY);
      this.isRestoringHome = false;
      flushCallbacks();
      return false;
    }
  }

  ensureEmpRoleForCompany(
    companyId: string,
    onSuccess: () => void,
    onError: (message: string) => void
  ): void {
    const targetId = companyId != null ? companyId.toString() : '';
    if (!targetId) {
      onError('Company not found for this invoice');
      return;
    }

    const existingRole = this.readDecrypted('emp_role');
    if (existingRole) {
      onSuccess();
      return;
    }

    this.fetchEmployeeCompanyDetails(targetId).subscribe(
      details => {
        if (details.employee_role) {
          localStorage.setItem('emp_role', this.eesdesService.set(details.employee_role));
        }
        if (details.employee_code) {
          localStorage.setItem('employee_code', this.eesdesService.set(details.employee_code));
        }
        onSuccess();
      },
      error => {
        const message =
          typeof error === 'string' ? error : error && error.message ? error.message : 'Unable to load employee role';
        onError(message);
      }
    );
  }

  switchToInvoiceCompany(
    companyId: string,
    companyRecord: GroupMailedInvoiceCompanyRecord,
    onSuccess: () => void,
    onError: (message: string) => void
  ): void {
    const targetId = companyId != null ? companyId.toString() : '';
    if (!targetId) {
      onError('Company not found for this invoice');
      return;
    }

    const currentId = this.getCurrentCompanyId();
    if (currentId && +targetId === +currentId) {
      onSuccess();
      return;
    }

    this.saveHomeCompanyContextIfNeeded();
    this.fetchEmployeeCompanyDetails(targetId)
      .switchMap(employeeDetails => {
        const details = this.normalizeCompanyRecord(
          {
            company_id: companyRecord.company_id != null && companyRecord.company_id !== ''
              ? companyRecord.company_id
              : targetId,
            company_title: companyRecord.company_title || employeeDetails.company_title,
            company_name: companyRecord.company_name || employeeDetails.company_name,
            company_code: companyRecord.company_code || employeeDetails.company_code,
            company_logo: companyRecord.company_logo || employeeDetails.company_logo,
            company_theme: companyRecord.company_theme || employeeDetails.company_theme,
            company_theme_text: companyRecord.company_theme_text || employeeDetails.company_theme_text,
          },
          targetId
        );
        return this.multiCompanyViewTokenService.getOrFetchTokens(targetId).map(tokens => ({
          details,
          tokens,
          empRole: employeeDetails.employee_role,
          employeeCode: employeeDetails.employee_code,
        }));
      })
      .subscribe(
        result => {
          this.applyCompanyStorage(
            result.details,
            result.tokens.access_token,
            result.tokens.user_access_token,
            result.empRole,
            result.employeeCode
          );
          this.multiCompanyViewTokenService.setActiveViewCompanyId(targetId);
          this.webService.clearApiCache();
          this.notifyHeaderRefresh();
          onSuccess();
        },
        error => {
          this.restoreHomeCompanyIfPresent();
          const message =
            typeof error === 'string' ? error : error && error.message ? error.message : 'Unable to switch company';
          onError(message);
        }
      );
  }

  private fetchEmployeeCompanyDetails(companyId: string): Observable<GroupMailedInvoiceCompanyRecord & {
    employee_role: string;
    employee_code: string;
  }> {
    if (this.eesdesService.get('ven_role', localStorage.getItem('ven_role')) === 'vendor') {
      return Observable.of({
        company_id: companyId,
        employee_role: 'vendor',
        employee_code: this.readDecrypted('employee_code'),
      });
    }

    const requestObj = {
      emp_id: this.localStorageService.getUserId(),
      company_id: '0',
    };

    return this.webService.commonPostMethod('employees/ajax_getsingemp_companies_details', requestObj).map(
      (data: any) => {
        if (!data || !data.status || !data.empdata || !Array.isArray(data.empdata.companies)) {
          throw 'Unable to load company employee details';
        }
        const match = data.empdata.companies.find(
          (c: any) => c.company_id != null && c.company_id.toString() === companyId.toString()
        );
        if (!match) {
          throw 'You do not have access to this company';
        }
        return {
          company_id: companyId,
          company_title: match.company_title || '',
          company_name: match.company_title || '',
          company_code: match.company_code || '',
          company_logo: match.company_logo || '',
          company_theme: match.company_theme || '#035168',
          company_theme_text: match.company_theme_text || '#FFFFFF',
          employee_role: match.employee_role || '',
          employee_code: match.employee_code || '',
        };
      }
    );
  }

  private saveHomeCompanyContextIfNeeded(): void {
    if (this.hasPendingHomeRestore()) {
      return;
    }
    const ctx: HomeCompanyContext = {
      company_id: this.getCurrentCompanyId(),
      company_title: this.readDecrypted('company_title'),
      company_code: this.readDecrypted('company_code'),
      company_logo: this.readDecrypted('company_logo'),
      company_theme: this.readDecrypted('company_theme'),
      company_theme_text: this.readDecrypted('company_theme_text'),
      emp_role: this.readDecrypted('emp_role'),
      employee_code: this.readDecrypted('employee_code'),
      access_token: this.readDecrypted('access_token'),
      user_access_token: this.readDecrypted('user_access_token'),
      session_company_id: sessionStorage.getItem('company_id') || this.getCurrentCompanyId(),
    };
    localStorage.setItem(GROUP_MAILED_INVOICE_HOME_CONTEXT_KEY, JSON.stringify(ctx));
  }

  private normalizeCompanyRecord(
    record: GroupMailedInvoiceCompanyRecord,
    companyId: string
  ): GroupMailedInvoiceCompanyRecord {
    const title = record.company_title || record.company_name || '';
    return {
      company_id: companyId,
      company_title: title,
      company_name: title,
      company_code: record.company_code || '',
      company_logo: record.company_logo || '',
      company_theme: (record.company_theme || '#035168').replace(/^\^/, ''),
      company_theme_text: (record.company_theme_text || '#FFFFFF').replace(/^\^/, ''),
    };
  }

  private applyCompanyStorage(
    record: GroupMailedInvoiceCompanyRecord,
    accessToken?: string,
    userAccessToken?: string,
    empRole?: string,
    employeeCode?: string,
    roleBeforeCompany: boolean = false
  ): void {
    const companyId = record.company_id != null ? record.company_id.toString() : '';

    const applyRoleAndEmployee = () => {
      if (empRole) {
        localStorage.setItem('emp_role', this.eesdesService.set(empRole));
      }
      if (employeeCode) {
        localStorage.setItem('employee_code', this.eesdesService.set(employeeCode));
      }
    };

    const applyCompanyFields = () => {
      sessionStorage.setItem('company_id', companyId);
      localStorage.setItem('company_id', this.eesdesService.set(companyId));
      localStorage.setItem('company_title', this.eesdesService.set(record.company_title || ''));
      localStorage.setItem('company_code', this.eesdesService.set(record.company_code || ''));
      localStorage.setItem('company_logo', this.eesdesService.set(record.company_logo || ''));
      localStorage.setItem('company_theme', this.eesdesService.set(record.company_theme || '#035168'));
      localStorage.setItem('company_theme_text', this.eesdesService.set(record.company_theme_text || '#FFFFFF'));
    };

    if (roleBeforeCompany) {
      applyRoleAndEmployee();
      applyCompanyFields();
    } else {
      applyCompanyFields();
      applyRoleAndEmployee();
    }

    if (accessToken) {
      Cookie.set('access_token', accessToken);
      document.cookie = 'access_token=' + accessToken + ';secure';
      localStorage.setItem('access_token', this.eesdesService.set(accessToken));
      this.setHttpCookie({
        action: 'set',
        cookie_name: 'access_token',
        cookie_value: this.eesdesService.set(accessToken, 'access_token'),
        expire: '72000'
      });
    }
    if (userAccessToken) {
      localStorage.setItem('user_access_token', this.eesdesService.set(userAccessToken));
    }
  }

  private setHttpCookie(payload: any): void {
    this.webService.commonPostMethod('login/cookies', payload).subscribe(
      () => {},
      () => {}
    );
  }

  private markRoleCheckSkipped(): void {
    sessionStorage.setItem(
      GROUP_MAILED_INVOICE_SKIP_ROLE_CHECK_KEY,
      String(Date.now() + 30000)
    );
  }

  private readDecrypted(key: string): string {
    const raw = localStorage.getItem(key);
    if (!raw || raw === 'undefined') {
      return '';
    }
    return this.eesdesService.get(key, raw) ? this.eesdesService.get(key, raw) : '';
  }

  private notifyHeaderRefresh(): void {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent(TYA_COMPANY_HEADER_REFRESH_EVENT));
    }
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src2648113480/src/src/app/shared/services/group-mailed-invoice-company-context.service.ts